import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageCta } from "@/components/PageParts";
import { usePageMeta } from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function CaseStudiesPage() {
  usePageMeta(
    "Case Studies — May Forward Tech",
    "Real client engagements from May Forward Tech: AI implementation, digital transformation and high-ROI marketing with headline outcomes."
  );
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/case-studies`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setStudies)
      .catch(() => setStudies([]));
  }, []);

  return (
    <>
      <section className="relative pt-[72px] bg-[#FAFAFC] overflow-hidden">
        <div className="hero-grid absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 lg:pt-24 lg:pb-16">
          <motion.p {...fadeUp} className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-6">
            Case studies
          </motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-[#0F172A] max-w-3xl">
            Proof, not promises
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-6 max-w-2xl text-lg text-slate-600 leading-relaxed">
            Real engagements, real numbers. Every story below is the kind of
            result we build our reputation on.
          </motion.p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[#F3F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {studies.length === 0 ? (
            <p className="text-slate-500 text-base text-center py-16">
              Case studies are being finalized — check back soon.
            </p>
          ) : (
            <div className="space-y-8 lg:space-y-10">
              {studies.map((s, i) => (
                <motion.article
                  key={s.id}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                  data-testid={`case-study-card-${s.id}`}
                  className={`bento-card group grid grid-cols-1 lg:grid-cols-2 rounded-2xl border border-slate-200 bg-white overflow-hidden ${
                    i % 2 === 1 ? "lg:[direction:rtl]" : ""
                  }`}
                >
                  {s.image_path ? (
                    <div className="min-h-[240px] lg:min-h-0 bg-white [direction:ltr]">
                      <img
                        src={`${BACKEND_URL}/api/files/${s.image_path}`}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        data-testid={`case-study-image-${s.id}`}
                      />
                    </div>
                  ) : (
                    <div className="hidden lg:block bg-[#F3F4F8] [direction:ltr]" />
                  )}
                  <div className="p-8 lg:p-12 flex flex-col [direction:ltr]">
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                      <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#0FA89E] border border-[rgba(15,168,158,0.25)] rounded-full px-3 py-1">
                        {s.service}
                      </span>
                      <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">
                        {s.industry}
                      </span>
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A] mb-2">
                      {s.title}
                    </h2>
                    <p className="text-sm font-medium text-slate-500 mb-7">{s.client}</p>
                    <div className="space-y-5 mb-9">
                      <div>
                        <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">
                          Challenge
                        </p>
                        <p className="text-base text-slate-600 leading-relaxed">{s.challenge}</p>
                      </div>
                      <div>
                        <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">
                          Result
                        </p>
                        <p className="text-base text-slate-600 leading-relaxed">{s.result}</p>
                      </div>
                    </div>
                    <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-6">
                      <span className="font-display text-xl sm:text-2xl font-bold text-[#F5A400]">
                        {s.metric}
                      </span>
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#0FA89E]"
                      >
                        Want results like this?
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <PageCta />
    </>
  );
}
